/**
 * deals.js — self-contained <deals-section> component (MA-1368).
 *
 * Drives the Deals page (sections/deals.liquid) for every MA market. Everything
 * the page needs lives in this one module: the API fetch, the card primitives
 * (icons, URL + percent formatting), the reward / date helpers, the card markup,
 * and the component. It imports only generic theme infrastructure — no store-card
 * module — so the Deals page owns its card rendering end to end.
 *
 *   1. Fetches every curated Featured Hot Deal for the active market on connect
 *      so the displayed Cashback / BV / IBV rates always reflect the current
 *      active rate at page load — never a cached page (bfcache restores
 *      re-fetch for the same reason).
 *   2. Renders one card per record — two market-driven layouts (a default
 *      coupon-style card with description + click-to-copy code chips, and a
 *      Taiwan card with expiry + "Get the Deal" button), both applying the
 *      reward-display rules (Cashback for Guest/PC, BV/IBV for PPC/UFO
 *      customers, Taiwan red cashback).
 *   3. Lists every deal on a single page (no "see all" and no pagination, per
 *      the MA-1368 BR). All cards share equal height (`tw:h-full` on the card
 *      shell) and stack two-per-row on mobile (the grid's `tw:grid-cols-2`).
 *
 * The section supplies localized status strings via data-i18n-* attributes; no
 * hardcoded user-facing English lives here.
 */

import { Component } from "@theme/component";
import { htmlEscape, pageUrl } from "@theme/utilities";
import { getCustomerRewardContext } from "@theme/price";
import { API_ENDPOINTS, EXTERNAL_HOSTS } from "@theme/api-endpoints";
import { getApiRequestContext } from "@theme/api-request-context";
import { getApiLocale } from "@theme/locale-map";
import { STORE_LOGO_BOX, STORE_LOGO_IMG } from "@theme/ng-store-card";

/** @param {string} key @param {string} fallback @returns {string} */
const t = (key, fallback) => Theme?.translations?.[key] || fallback;

/* ─────────────────────────── card primitives ─────────────────────────── */

/**
 * Taiwan renders the cashback value in red. The live value comes from the
 * theme's `cashback_text_color` setting via the section's
 * `data-cashback-text-color` attribute; this is the fallback when that setting
 * is blank — same hex as `cart-items__cashback--twn`.
 */
const TWN_CASHBACK_COLOR = "#E53935";

/** Teal cashback "coin" icon — 10px on mobile, 12px on desktop. */
const CASHBACK_ICON = `<svg class="tw:h-2.5 tw:w-2.5 tw:lg:h-3 tw:lg:w-3 tw:shrink-0" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 11.8a5.8 5.8 0 1 0 0-11.6 5.8 5.8 0 0 0 0 11.6Z" fill="#09A79D"/><path d="M8.924 7.035c0-1.352-1.252-1.6-2.428-1.756V3.735c.22.03.424.13.583.283.16.154.267.355.305.573h1.324c-.124-1.352-1.292-1.74-2.228-1.848V1.7H5.504v1.011c-1.292.088-2.304.612-2.304 1.836 0 1.224 1.16 1.544 2.32 1.692v1.76a1.07 1.07 0 0 1-.734-.344 1.07 1.07 0 0 1-.286-.756H3.076c0 1.124.72 1.92 2.428 2.06v1.26h.992v-1.272c1.168-.088 2.428-.548 2.428-1.912ZM4.524 4.443c0-.464.4-.716.988-.772v1.476c-.588-.1-1.004-.268-1.004-.704h.016ZM6.524 8.011V6.399c.624.116 1.08.308 1.08.772s-.512.76-1.108.828l.028.012Z" fill="#fff"/></svg>`;

/** Arrow-outward icon next to the store name — 16px mobile / 20px desktop. */
const ARROW_OUTWARD_ICON = `<svg class="tw:h-4 tw:w-4 tw:lg:h-5 tw:lg:w-5 tw:shrink-0" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5.833 14.167 14.167 5.833M14.167 5.833H6.667M14.167 5.833v7.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/**
 * Normalises a reward percentage to a display string with no trailing zeros
 * ("3" → "3%", "2.50%" → "2.5%"). Returns '' when not a positive number.
 *
 * @param {unknown} value
 * @returns {string}
 */
function formatPercent(value) {
  const n = parseFloat(String(value ?? "").replace("%", ""));
  if (!Number.isFinite(n) || n <= 0) return "";
  return `${parseFloat(n.toFixed(2))}%`;
}

/**
 * Resolves a store link to an absolute URL. NG API store links are relative
 * shop.com paths — resolve them against shop.com so they never hit the
 * storefront domain; absolute URLs pass through verbatim.
 *
 * @param {string} rawLink
 * @returns {string}
 */
function resolveStoreUrl(rawLink) {
  if (!rawLink) return "";
  return /^https?:\/\//i.test(rawLink) ? rawLink : `${EXTERNAL_HOSTS.shopCom}${rawLink}`;
}

/**
 * "up to X%" fragment for a reward line. Deal rates never carry a struck-through
 * pre-campaign rate, so a single display percent is all that's needed.
 *
 * @param {string} percent
 * @returns {string}
 */
function rewardPercentHtml(percent) {
  return `${htmlEscape(t("store_card_up_to", "up to"))} ${htmlEscape(percent)}`;
}

/* ─────────────────────────────── API ─────────────────────────────── */

/**
 * Extracts the record array from the response body. The service returns
 * either a bare array of records or an object wrapping one — tolerate both.
 *
 * @param {unknown} json
 * @returns {Array<Record<string, any>>}
 */
function extractDeals(json) {
  if (Array.isArray(json)) return json;
  if (json && typeof json === "object") {
    for (const key of ["featuredHotDeals", "hotDeals", "deals", "records", "hits", "items", "data"]) {
      const value = /** @type {Record<string, unknown>} */ (json)[key];
      if (Array.isArray(value)) return value;
    }
  }
  return [];
}

/**
 * Fetches the MA Featured Hot Deals for the active market.
 *
 *   GET {API_ENDPOINTS.featuredHotDeals}?isDistributor=<bool>
 *   Headers: AMOS-Country, AMOS-Language, AMOS-SiteType: W
 *
 * The App Proxy authenticates the call itself (no API key). A `204 No Content`
 * is a valid "no featured deals for this market" result, not an error.
 *
 * **SGP is hardcoded to English:** the FeaturedHotDeals API does not support any
 * language other than ENG on the SGP market (non-ENG requests return an empty
 * array). So for `AMOS-Country: SGP` we always send `AMOS-Language: ENG`,
 * regardless of the storefront's selected language.
 *
 * @param {AbortSignal} signal
 * @returns {Promise<{ ok: true, deals: Array<Record<string, any>> } | { ok: false, message: string }>}
 * @throws {DOMException} Re-throws AbortError so the caller can ignore cancellations.
 */
async function fetchFeaturedHotDeals(signal) {
  const { languageCode, countryCode } = getApiLocale();
  const context = getApiRequestContext(countryCode, "W");
  const isDistributor = context.distributorInfo.isDistributor;

  // SGP only supports ENG on this API — force English regardless of the
  // storefront language so Chinese-locale SGP users still see deals.
  const apiLanguage = countryCode === "SGP" ? "ENG" : languageCode;

  const response = await fetch(`${API_ENDPOINTS.featuredHotDeals}?isDistributor=${isDistributor}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "AMOS-Country": countryCode,
      "AMOS-Language": apiLanguage,
      "AMOS-SiteType": "W",
    },
    signal,
  });

  if (response.status === 204) return { ok: true, deals: [] };

  if (!response.ok) {
    return { ok: false, message: `Featured hot deals request failed (${response.status})` };
  }

  return { ok: true, deals: extractDeals(await response.json()) };
}

/* ───────────────────────── reward / date helpers ───────────────────────── */

/**
 * Parses the API's `"YYYY-MM-DD hh:mm:ss"` timestamps. Normalised to a
 * `T`-separated ISO string so Safari parses it too. Returns null for empty
 * or unparseable values.
 *
 * @param {unknown} value
 * @returns {Date | null}
 */
function parseApiDate(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const date = new Date(raw.replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Resolves a deal's promotion expiry. The feed is inconsistent about which
 * field carries it: some records populate `campaignEndDate`, others leave it
 * blank and carry the date in `endDate` — both in `"YYYY-MM-DD hh:mm:ss"`.
 *
 * @param {Record<string, any> | null | undefined} deal
 * @returns {Date | null}
 */
function dealExpiry(deal) {
  return parseApiDate(deal?.campaignEndDate) ?? parseApiDate(deal?.endDate);
}

/**
 * Formats a promotion expiry date in the storefront locale (e.g. 07/20/2026
 * on en, 2026/07/20 on zh-TW).
 *
 * @param {Date} date
 * @returns {string}
 */
function formatExpiryDate(date) {
  const lang = document.documentElement.lang || "en";
  try {
    return new Intl.DateTimeFormat(lang, { year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
  } catch {
    return date.toLocaleDateString();
  }
}

/**
 * Normalises a rate to a display string. Prefers the API's display-ready
 * string (which carries flat-amount formats for non-percentage commission
 * stores) trimmed of trailing zeros when it is a plain percent; falls back
 * to formatting the numeric value. A zero-valued rate renders nothing.
 *
 * @param {unknown} display - Display-ready string (e.g. "27.00%").
 * @param {unknown} numeric - Numeric rate (e.g. 27.0).
 * @returns {string}
 */
function rateDisplay(display, numeric) {
  const raw = String(display ?? "").trim();
  if (raw) {
    const rawValue = parseFloat(raw.replace(/[^0-9.-]+/g, ""));
    if (Number.isFinite(rawValue) && rawValue <= 0) return "";
    // "27.00%" → "27%"; non-percent strings (flat amounts, custom copy) pass through.
    return /^[\d.,]+\s*%$/.test(raw) ? formatPercent(raw) : raw;
  }
  return formatPercent(numeric);
}

/**
 * Cashback rate for Guest/PC customers. An expired campaign reverts to the
 * standard rate; otherwise the current active rate is used.
 *
 * @param {Record<string, any>} deal
 * @returns {string}
 */
function extractCashback(deal) {
  const current = rateDisplay(deal.cashBackDisplay, deal.cashBack);
  const standard = rateDisplay(deal.standardCashbackDisplay, deal.standardCashback);
  const expiresAt = dealExpiry(deal);
  const expired = Boolean(expiresAt) && expiresAt.getTime() <= Date.now();
  return expired ? standard || current : current;
}

/**
 * IBV rate for IBV-eligible customers — Increased IBV when the customer's
 * reward-structure preference selects it, standard otherwise.
 *
 * @param {Record<string, any>} deal
 * @param {ReturnType<typeof getCustomerRewardContext>} ctx
 * @returns {string}
 */
function extractIbv(deal, ctx) {
  if (!ctx.canShowIbv) return "";
  const current = ctx.isIbvOnly
    ? rateDisplay(deal.ibvOnlyPercentDisplay, deal.ibvOnlyPercent)
    : rateDisplay(deal.ibvPercentPointsDisplay, deal.ibvPercent);
  const standard = ctx.isIbvOnly
    ? rateDisplay(deal.standardIbvOnlyPercentDisplay, deal.standardIbvOnlyPercent)
    : rateDisplay(deal.standardIbvPercentDisplay, deal.standardIbvPercent);
  const expiresAt = dealExpiry(deal);
  const expired = Boolean(expiresAt) && expiresAt.getTime() <= Date.now();
  return expired ? standard || current : current;
}

/**
 * BV rate for BV/IBV customers — only when the record carries a positive rate.
 *
 * @param {Record<string, any>} deal
 * @param {ReturnType<typeof getCustomerRewardContext>} ctx
 * @returns {string}
 */
function extractBv(deal, ctx) {
  if (!ctx.isBvIbvCustomer) return "";
  return rateDisplay(deal.bvPercentPointsDisplay, deal.bvPercent);
}

/* ──────────────────────────── card rendering ──────────────────────────── */

/** Whether the deal card prints its "Expires: <date>" line. Off while the feed's
 *  dates carry no real urgency; the date keeps driving everything else. */
const SHOW_EXPIRY_DATE = false;

/** Reward-line classes — wrapping (no `whitespace-nowrap`) since the card
 * shell is `overflow-hidden` and carries multiple content lines. */
const REWARD_LINE_CLASS =
  "tw:flex tw:max-w-full tw:flex-wrap tw:items-center tw:justify-center tw:gap-1 tw:text-[12px] tw:font-medium tw:leading-4 tw:text-coal tw:lg:font-normal";

/** Shared card-shell classes for both layouts. */
const CARD_SHELL =
  "tw:flex tw:h-full tw:flex-col tw:items-center tw:gap-2.5 tw:rounded-[20px] tw:border tw:border-[#E6E6E6] tw:bg-white tw:p-5 tw:text-coal tw:no-underline tw:transition-shadow tw:hover:shadow-md";

/**
 * Whether a deal record is a walk-in store. The feed sends `isWalkIn` as a
 * boolean, but tolerate a "true" string too.
 *
 * @param {Record<string, any>} deal
 * @returns {boolean}
 */
function isWalkInDeal(deal) {
  return deal.isWalkIn === true || String(deal.isWalkIn ?? "").trim().toLowerCase() === "true";
}

/**
 * Resolves the link-off destination for a deal record.
 * - walk-in stores (AI-113087): the API's own `url`, verbatim (relative
 *   shop.com paths resolved against shop.com) — no interstitial.
 * - dealType "b" (banner ad, MA-2482): the affiliate interstitial, which
 *   link-offs via /linkoff/v1/affiliate with the deal's bannerId as adId.
 * - otherwise a volumeId routes through the store interstitial (MA-1358);
 *   "xservice" deals have bannerId === volumeId and stay on this path.
 * - fallback: the legacy shop.com link-off URL.
 *
 * @param {Record<string, any>} deal
 * @returns {string}
 */
function resolveDealUrl(deal) {
  if (isWalkInDeal(deal)) {
    const walkInUrl = resolveStoreUrl(String(deal.url ?? "").trim());
    if (walkInUrl) return walkInUrl;
  }
  const isBannerDeal = String(deal.dealType ?? "").trim().toLowerCase() === "b";
  const bannerId = String(deal.bannerId ?? "").trim();
  if (isBannerDeal && /^\d+$/.test(bannerId)) {
    return pageUrl("affiliate-interstitial", { adId: bannerId });
  }
  const volumeId = String(deal.volumeId ?? "").trim();
  if (volumeId) return pageUrl("store-interstitial", { storeId: volumeId });
  return resolveStoreUrl(deal.url || deal.linkOffUrl || "") || "#";
}

/**
 * Builds the reward line HTML for a deal, respecting customer type and the
 * `overrideRewardDisplay` escape hatch. Cashback shows for Guest/PC users,
 * and also for PPC (distributor) users in US/CA alongside their BV/IBV lines.
 * BV/IBV customers outside US/CA see only volume rewards. Taiwan renders the
 * cashback line (text + coin icon) red.
 *
 * @param {Record<string, any>} deal
 * @param {ReturnType<typeof getCustomerRewardContext>} ctx
 * @param {string} cashbackColor - Taiwan cashback text/icon color.
 * @returns {string}
 */
function rewardHtml(deal, ctx, cashbackColor) {
  const override = String(deal.overrideRewardDisplay ?? "").trim();
  if (override) return `<span class="${REWARD_LINE_CLASS}">${htmlEscape(override)}</span>`;

  if (ctx.isBvIbvCustomer) {
    const bv = extractBv(deal, ctx);
    const ibv = extractIbv(deal, ctx);
    const bvHtml = bv
      ? `<span class="${REWARD_LINE_CLASS}">${rewardPercentHtml(bv)} ${htmlEscape(t("store_card_bv", "BV"))}</span>`
      : "";
    const ibvHtml = ibv
      ? `<span class="${REWARD_LINE_CLASS}">${rewardPercentHtml(ibv)} ${htmlEscape(t("store_card_ibv", "IBV"))}</span>`
      : "";

    let cashbackHtml = "";
    if (ctx.isPpc && ctx.isUsOrCanada) {
      const cashback = extractCashback(deal);
      if (cashback) {
        cashbackHtml = `<span class="${REWARD_LINE_CLASS}">${rewardPercentHtml(cashback)} ${CASHBACK_ICON} ${htmlEscape(t("store_card_cashback", "Cashback"))}</span>`;
      }
    }

    return `${bvHtml}${ibvHtml}${cashbackHtml}`;
  }

  const cashback = extractCashback(deal);
  if (!cashback) return "";

  const isTwn = ctx.countryCode === "TW";
  const color = cashbackColor || TWN_CASHBACK_COLOR;
  const twnStyle = isTwn ? ` style="color:${color}"` : "";
  const icon = isTwn ? CASHBACK_ICON.replace(/#09A79D/g, color) : CASHBACK_ICON;
  return `<span class="${REWARD_LINE_CLASS}"${twnStyle}>${rewardPercentHtml(cashback)} ${icon} ${htmlEscape(t("store_card_cashback", "Cashback"))}</span>`;
}

/**
 * Taiwan layout — logo-in-box, name + inline arrow, reward, expiry, button.
 *
 * @param {Record<string, any>} deal
 * @param {ReturnType<typeof getCustomerRewardContext>} ctx
 * @param {string} storeUrl
 * @param {string} logoHtml
 * @param {string} name
 * @param {string} cashbackColor
 * @param {string} showPromotionalText
 * @returns {string}
 */
function renderTaiwanCard(deal, ctx, storeUrl, logoHtml, name, cashbackColor, showPromotionalText) {
  const description = String(deal.promotionalText ?? "").trim();
  const descriptionHtml = showPromotionalText == "true" ? description
    ? `<span class="tw:max-w-full tw:wrap-break-word tw:text-center tw:text-[12px] tw:font-normal tw:leading-4 tw:text-[#6C6C6C] tw:lg:text-[13px] tw:lg:leading-5">${htmlEscape(description)}</span>`
    : "" : "";

  // The expiry line is held back at MA's request — the feed's dates sit far enough out
  // that the line reads as noise rather than urgency. The date itself is untouched and
  // still drives the expired-deal filter and the revert to standard reward rates, so
  // setting SHOW_EXPIRY_DATE back to true restores the line exactly as it was.
  const expiresAt = dealExpiry(deal);
  const expiryHtml =
    SHOW_EXPIRY_DATE && expiresAt && expiresAt.getTime() > Date.now()
      ? `<span class="tw:text-[12px] tw:font-medium tw:leading-4 tw:lg:text-[13px]">${htmlEscape(t("store_card_expires", "Expires"))}: ${htmlEscape(formatExpiryDate(expiresAt))}</span>`
      : "";

  return `<li class="tw:m-0 tw:min-w-0 tw:list-none tw:p-0">
    <a href="${htmlEscape(storeUrl)}" target="_blank" rel="noopener noreferrer" class="${CARD_SHELL}">
      <span class="${STORE_LOGO_BOX} tw:rounded-[10px] tw:border tw:border-[#F0F0F0] tw:px-2.5 tw:lg:px-2.5">
        ${logoHtml}
      </span>
      <span class="tw:flex tw:min-w-0 tw:max-w-full tw:items-center tw:gap-1 tw:text-[16px] tw:font-medium tw:leading-normal tw:lg:text-[18px] tw:lg:font-normal">
        <span class="tw:line-clamp-2 tw:min-w-0 tw:wrap-break-word">${name}</span>
        ${ARROW_OUTWARD_ICON}
      </span>
      ${rewardHtml(deal, ctx, cashbackColor)}
      ${descriptionHtml}
      ${expiryHtml}
      <span class="tw:mt-auto tw:w-full tw:rounded-full tw:border tw:border-[#3D3F5A] tw:px-4 tw:py-2 tw:text-center tw:text-[13px] tw:font-bold tw:leading-4 tw:text-[#343650] tw:lg:text-[14px]">
        ${htmlEscape(t("deals_get_the_deal", "Get the Deal"))}
      </span>
    </a>
  </li>`;
}

/**
 * Default layout — top-right arrow, centered logo, store name, reward,
 * description, click-to-copy coupon-code chip(s).
 *
 * The store name sits between the logo and the reward line, same ordering as
 * the Taiwan card (MA-2866). It carries no inline arrow of its own — this
 * layout's "opens elsewhere" cue is the arrow pinned to the card's top-right.
 *
 * @param {Record<string, any>} deal
 * @param {ReturnType<typeof getCustomerRewardContext>} ctx
 * @param {string} storeUrl
 * @param {string} logoHtml
 * @param {string} name - Escaped store name.
 * @param {string} cashbackColor
 * @param {string} showPromotionalText
 * @returns {string}
 */
function renderDefaultCard(deal, ctx, storeUrl, logoHtml, name, cashbackColor, showPromotionalText) {
  const description = String(deal.promotionalText ?? "").trim();
  const descriptionHtml = showPromotionalText == "true" ? description
    ? `<span class="tw:max-w-full tw:wrap-break-word tw:text-center tw:text-[12px] tw:font-normal tw:leading-4 tw:text-[#6C6C6C] tw:lg:text-[13px] tw:lg:leading-5">${htmlEscape(description)}</span>`
    : "" : "";

  // Coupon code(s) — dashed chips. `couponCode` is usually a single value;
  // comma-split tolerates a multi-code string. Chips flex to fill the row so a
  // lone code spans the card while two codes split it evenly. Each chip is
  // click-to-copy (and keyboard-activatable) via the delegated grid listener.
  const codes = String(deal.couponCode ?? "")
    .split(",")
    .map(code => code.trim())
    .filter(Boolean);
  const copiedLabel = htmlEscape(t("deals_copied", "Copied!"));
  const codeHtml = codes.length
    ? `<div class="tw:relative tw:z-10 tw:mt-auto tw:flex tw:w-full tw:flex-wrap tw:items-stretch tw:justify-center tw:gap-2 tw:pt-1">
        ${codes
          .map(
            code =>
              `<span role="button" tabindex="0" data-copy-code="${htmlEscape(code)}" data-copied-label="${copiedLabel}" class="tw:cursor-pointer tw:min-w-[80px] tw:flex-1 tw:rounded-lg tw:border tw:border-dashed tw:border-[#ACACAC] tw:px-3 tw:py-2 tw:text-center tw:text-[14px] tw:font-bold tw:leading-4 tw:text-[#343650] tw:lg:text-[15px]">${htmlEscape(code)}</span>`
          )
          .join("")}
      </div>`
    : "";

  const nameHtml = name
    ? `<span class="tw:line-clamp-2 tw:min-w-0 tw:max-w-full tw:wrap-break-word tw:text-center tw:text-[16px] tw:font-medium tw:leading-normal tw:lg:text-[18px] tw:lg:font-normal">${name}</span>`
    : "";

  return `<li class="tw:m-0 tw:min-w-0 tw:list-none tw:p-0">
    <a href="${htmlEscape(storeUrl)}" target="_blank" rel="noopener noreferrer" class="${CARD_SHELL} tw:relative tw:pt-9">
      <span class="tw:absolute tw:right-4 tw:top-4 tw:text-coal">${ARROW_OUTWARD_ICON}</span>
      <span class="${STORE_LOGO_BOX}">
        ${logoHtml}
      </span>
      ${nameHtml}
      ${rewardHtml(deal, ctx, cashbackColor)}
      ${descriptionHtml}
      ${codeHtml}
    </a>
  </li>`;
}

/**
 * Renders a single deal card `<li>` HTML string. Chooses the Taiwan layout in
 * the TW market, the coupon layout everywhere else.
 *
 * @param {Record<string, any>} deal - One record from the FeaturedHotDeals response.
 * @param {ReturnType<typeof getCustomerRewardContext>} ctx
 * @param {string} cashbackColor - Taiwan cashback color from the section settings.
 * @param {string} showPromotionalText
 * @returns {string}
 */
function renderDealCard(deal, ctx, cashbackColor, showPromotionalText) {
  if (!deal) return "";

  const name = htmlEscape(deal.title || "");
  const logoUrl = deal.imageSrc || deal.storeLogo || "";
  const logoAlt = htmlEscape(deal.altText || "") || name;
  const storeUrl = resolveDealUrl(deal);

  const logoHtml = logoUrl
    ? `<img src="${htmlEscape(logoUrl)}" alt="${logoAlt}" loading="lazy" class="${STORE_LOGO_IMG}">`
    : `<span class="tw:text-[16px] tw:font-medium tw:text-coal" aria-hidden="true">${name.charAt(0)}</span>`;

  return ctx.countryCode === "TW"
    ? renderTaiwanCard(deal, ctx, storeUrl, logoHtml, name, cashbackColor, showPromotionalText)
    : renderDefaultCard(deal, ctx, storeUrl, logoHtml, name, cashbackColor, showPromotionalText);
}

/**
 * A single deal-card skeleton placeholder, matching the deal card `<li>`
 * silhouette (the shared card shell around a logo strip, a name/reward line
 * and a code-chip line). Same `animate-pulse` treatment as the store-card
 * skeleton; `aria-hidden` (the busy state is conveyed by `aria-busy` on the
 * grid container).
 *
 * @returns {string} one `<li>` skeleton
 */
function renderDealCardSkeleton() {
  return `<li class="tw:m-0 tw:min-w-0 tw:list-none tw:p-0" aria-hidden="true">
    <div class="${CARD_SHELL}">
      <span class="tw:h-31.25 tw:w-full tw:animate-pulse tw:rounded-[10px] tw:bg-[#f4f4f4] tw:motion-reduce:animate-none"></span>
      <span class="tw:h-4 tw:w-3/4 tw:animate-pulse tw:rounded tw:bg-[#f4f4f4] tw:motion-reduce:animate-none"></span>
      <span class="tw:h-3 tw:w-1/2 tw:animate-pulse tw:rounded tw:bg-[#f4f4f4] tw:motion-reduce:animate-none"></span>
      <span class="tw:mt-auto tw:h-9 tw:w-full tw:animate-pulse tw:rounded-lg tw:bg-[#f4f4f4] tw:motion-reduce:animate-none"></span>
    </div>
  </li>`;
}

/**
 * `count` deal-card skeletons joined — for seeding the grid while the
 * Featured Hot Deals fetch is in flight.
 *
 * @param {number} [count=10]
 * @returns {string}
 */
function renderDealCardSkeletons(count = 10) {
  return Array.from({ length: Math.max(1, count) }, renderDealCardSkeleton).join("");
}

/* ─────────────────────────────── component ─────────────────────────────── */

/**
 * @typedef {object} Refs
 * @property {HTMLElement} grid - The deal card <ul>.
 * @property {HTMLElement} status - The loading / empty / error status line.
 *
 * @extends {Component<Refs>}
 */
class DealsSection extends Component {
  requiredRefs = ["grid", "status"];

  /** @type {AbortController | null} */
  #abortController = null;

  /** Brief "Copied!" feedback duration on a coupon-code chip, in ms. */
  #copiedTimeoutMs = 1500;

  /**
   * bfcache restores show the DOM exactly as it was left — including
   * potentially stale promotional rates. Re-fetch so rates always reflect
   * the current active values.
   *
   * @param {PageTransitionEvent} event
   */
  #onPageShow = event => {
    if (event.persisted) this.#load();
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("pageshow", this.#onPageShow);
    // Delegated so it covers the coupon-code chips injected after each fetch.
    this.refs.grid.addEventListener("click", this.#onGridClick);
    this.refs.grid.addEventListener("keydown", this.#onGridKeydown);
    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("pageshow", this.#onPageShow);
    this.refs.grid.removeEventListener("click", this.#onGridClick);
    this.refs.grid.removeEventListener("keydown", this.#onGridKeydown);
    this.#abortController?.abort();
  }

  /**
   * Click-to-copy for coupon-code chips. A chip sits inside the card's
   * link-off anchor, so we stop the click from navigating and copy the code
   * to the clipboard with brief "Copied!" feedback.
   *
   * @param {MouseEvent} event
   */
  #onGridClick = event => {
    const chip = this.#chipFrom(event);
    if (!chip) return;
    event.preventDefault();
    event.stopPropagation();
    this.#copyCode(chip);
  };

  /**
   * Keyboard activation (Enter / Space) for the role="button" code chips.
   *
   * @param {KeyboardEvent} event
   */
  #onGridKeydown = event => {
    if (event.key !== "Enter" && event.key !== " ") return;
    const chip = this.#chipFrom(event);
    if (!chip) return;
    event.preventDefault();
    event.stopPropagation();
    this.#copyCode(chip);
  };

  /**
   * Resolves the coupon-code chip an event originated from, if any.
   *
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #chipFrom(event) {
    const target = event.target instanceof Element ? event.target : null;
    return /** @type {HTMLElement | null} */ (target?.closest("[data-copy-code]") ?? null);
  }

  /**
   * Copies the chip's code to the clipboard and shows "Copied!" briefly.
   *
   * @param {HTMLElement} chip
   */
  #copyCode(chip) {
    const code = chip.dataset.copyCode;
    if (!code || !navigator.clipboard) return;
    if (chip.dataset.copyActive === "true") return;

    navigator.clipboard
      .writeText(code)
      .then(() => {
        const original = chip.textContent;
        chip.dataset.copyActive = "true";
        chip.textContent = chip.dataset.copiedLabel || "Copied!";
        setTimeout(() => {
          chip.textContent = original;
          delete chip.dataset.copyActive;
        }, this.#copiedTimeoutMs);
      })
      .catch(() => {
        // Clipboard write failed (e.g. permissions) — leave the chip as-is.
      });
  }

  /** @param {string} key @returns {string} */
  #i18n(key) {
    return this.getAttribute(`data-i18n-${key}`) || "";
  }

  /**
   * Shows the status line (loading / empty / error) and hides the grid, or
   * vice versa when `message` is empty.
   *
   * @param {string} message
   */
  #setStatus(message) {
    const { grid, status } = this.refs;
    status.textContent = message;
    status.hidden = !message;
    grid.hidden = Boolean(message);
  }

  /**
   * Paints deal-card skeletons into the grid while the fetch is in flight,
   * instead of hiding the grid behind a "Loading…" text line. Real cards
   * replace them in #load. Loading visual only — no behaviour change.
   */
  #showLoading() {
    const { grid, status } = this.refs;
    status.hidden = true;
    status.textContent = "";
    grid.hidden = false;
    grid.setAttribute("aria-busy", "true");
    grid.innerHTML = renderDealCardSkeletons();
  }

  async #load() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#showLoading();

    let result;
    try {
      result = await fetchFeaturedHotDeals(this.#abortController.signal);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.warn("[deals]", error);
      this.#setStatus(this.#i18n("error"));
      return;
    }

    if (!result.ok) {
      console.warn("[deals]", result.message);
      this.#setStatus(this.#i18n("error"));
      return;
    }

    const ctx = getCustomerRewardContext();

    // The service returns records pre-curated for the market; honour its
    // merchandising order via sortOrder.
    let deals = [...result.deals].sort((a, b) => (Number(a?.sortOrder) || 0) - (Number(b?.sortOrder) || 0));

    // Taiwan: hide deals whose promotion has already expired (TW deals are
    // time-limited cashback promotions; expired ones must not render).
    if (ctx.countryCode === "TW") {
      const now = Date.now();
      deals = deals.filter(deal => {
        const expiresAt = dealExpiry(deal);
        return !expiresAt || expiresAt.getTime() > now;
      });
    }

    if (!deals.length) {
      this.#setStatus(this.#i18n("empty"));
      return;
    }

    this.#setStatus("");
    // Cashback color comes from the theme's `cashback_text_color` setting so each
    // market can tune it in the editor; TWN_CASHBACK_COLOR covers a blank setting.
    const cashbackColor = this.getAttribute("data-cashback-text-color")?.trim() || TWN_CASHBACK_COLOR;
    const showPromotionalText = this.getAttribute("data-display-promotional-text") || "";
    this.refs.grid.innerHTML = deals.map(deal => renderDealCard(deal, ctx, cashbackColor, showPromotionalText)).join("");
    this.refs.grid.removeAttribute("aria-busy");
  }
}

if (!customElements.get("deals-section")) {
  customElements.define("deals-section", DealsSection);
}
